import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Home, MessageSquare, Calendar, Sun, Moon, LogOut, Shield, Zap, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { NAV, APP } from "../constants/testIds";
import { SparkLogo } from "./Spark";
import { PlanBadge } from "./plan/PlanUI";
import Onboarding from "./Onboarding";

export function Layout({ children }) {
  const { user, logout } = useAuth();
  // Sair só depois de confirmar — clique fora, Esc e Cancelar abortam.
  const [confirmLogout, setConfirmLogout] = useState(false);
  // Primeira entrada: mostra o mini-questionário que ativa a personalização.
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get("/profile")
      .then((r) => { if (!r.data.onboarding_done) setShowOnboarding(true); })
      .catch(() => {});
    // roda 1x por sessão de layout — o PUT do onboarding persiste o done
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem("antonio_theme");
    if (stored) return stored === "dark";
    return true; // default dark
  });
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("antonio_theme", dark ? "dark" : "light");
  }, [dark]);

  const isChat = loc.pathname.startsWith("/chat");

  return (
    <div className="min-h-screen bg-background bg-depth text-foreground flex flex-col">
      {/* Ambient violet glow background — camadas do mesmo tom pra dar profundidade */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] h-[500px] w-[500px] rounded-full bg-violet-600/15 blur-[120px]" />
        <div className="absolute top-[30%] right-[-12%] h-[400px] w-[400px] rounded-full bg-indigo-600/10 blur-[130px]" />
        <div className="absolute bottom-[-15%] right-[-10%] h-[450px] w-[450px] rounded-full bg-fuchsia-600/10 blur-[120px]" />
      </div>

      {/* Top bar (desktop) / minimal header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <SparkLogo size="default" />
          <nav className="hidden md:flex items-center gap-1">
            <DesktopLink to="/" testId={NAV.hub} icon={Home} label="Hub" />
            <DesktopLink to="/chat" testId={NAV.chat} icon={MessageSquare} label="Chat" />
            <DesktopLink to="/rotina" testId={NAV.rotina} icon={Calendar} label="Rotina" />
            <DesktopLink to="/perfil" testId="nav-perfil" icon={UserRound} label="Perfil" />
            {user?.is_admin && <DesktopLink to="/admin" testId="nav-admin" icon={Shield} label="Admin" />}
          </nav>
          <div className="flex items-center gap-1.5">
            {user?.plano === "gratis" && (
              <Link
                to="/planos"
                data-testid="header-upgrade-btn"
                className="hidden sm:inline-flex items-center gap-1 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium px-3 py-1.5 transition"
              >
                <Zap className="h-3 w-3" /> Fazer upgrade
              </Link>
            )}
            <PlanBadge plano={user?.plano} />
            <button
              data-testid={APP.themeToggle}
              onClick={() => setDark((d) => !d)}
              className="rounded-full p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title={dark ? "Tema claro" : "Tema escuro"}
            >
              {dark ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
            </button>
            <button
              data-testid={APP.logout}
              onClick={() => setConfirmLogout(true)}
              className="rounded-full p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Sair"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className={`flex-1 ${isChat ? "" : "mx-auto w-full max-w-6xl px-4 sm:px-6 py-6 pb-28 md:pb-6"}`}>
        {children}
      </main>

      {/* Bottom nav (mobile only) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 pb-[max(env(safe-area-inset-bottom),0.5rem)] px-4">
        <div className="mx-auto flex max-w-md items-center justify-around gap-1 rounded-full glass shadow-xl shadow-black/30 px-2 py-1.5">
          <MobileLink to="/" testId={NAV.hub} icon={Home} label="Hub" />
          <MobileLink to="/chat" testId={NAV.chat} icon={MessageSquare} label="Chat" />
          <MobileLink to="/rotina" testId={NAV.rotina} icon={Calendar} label="Rotina" />
          <MobileLink to="/perfil" testId="nav-perfil" icon={UserRound} label="Perfil" />
        </div>
      </nav>

      {showOnboarding && (
        <Onboarding
          onDone={(sugestao) => {
            setShowOnboarding(false);
            // Quem respondeu cai direto no chat com a primeira pergunta já engatilhada
            if (sugestao) navigate("/chat", { state: { sugestao } });
          }}
        />
      )}

      {confirmLogout && (
        <LogoutConfirm
          onCancel={() => setConfirmLogout(false)}
          onConfirm={() => { setConfirmLogout(false); logout(); }}
        />
      )}
    </div>
  );
}

function LogoutConfirm({ onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      data-testid="logout-confirm-overlay"
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        data-testid="logout-confirm-modal"
        role="dialog"
        aria-modal="true"
        className="w-full max-w-sm rounded-2xl border border-border bg-popover p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <div className="rounded-full bg-secondary p-2">
            <LogOut className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
          <h2 className="font-bold tracking-tight">Tem certeza que quer sair?</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Seu histórico e seu perfil ficam salvos — é só entrar de novo quando quiser.
        </p>
        <div className="flex justify-end gap-2">
          <button
            data-testid="logout-cancel-btn"
            onClick={onCancel}
            className="rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            Cancelar
          </button>
          <button
            data-testid="logout-confirm-btn"
            onClick={onConfirm}
            className="rounded-full bg-red-600 hover:bg-red-500 text-white px-4 py-2 text-sm font-medium transition-colors"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}

function DesktopLink({ to, testId, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "bg-violet-500/15 text-violet-300 dark:text-violet-300"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`
      }
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} />
      {label}
    </NavLink>
  );
}

function MobileLink({ to, testId, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={`${testId}-mobile`}
      className={({ isActive }) =>
        `flex flex-1 flex-col items-center gap-0.5 rounded-full px-3 py-2 text-xs transition-colors ${
          isActive ? "bg-violet-500/20 text-violet-300" : "text-muted-foreground"
        }`
      }
    >
      <Icon className="h-5 w-5" strokeWidth={1.75} />
      <span className="text-[10px] font-medium tracking-wide">{label}</span>
    </NavLink>
  );
}
